import { Loader } from "@circle-react-uikit/Loader";
import { usePunditUserContext } from "@circle-react/contexts/punditUserContext";
import { isCommunityAdmin } from "@circle-react/helpers/communityMemberHelpers";
import { usePlanFeatureFlags } from "@circle-react/hooks/usePlanFeatureFlags";
import { CodeInput } from "@circleco/compass/components/CodeInput";
import { FormField } from "@circleco/compass/components/FormField";
import { Controller, useFormContext } from "react-hook-form";

import { t } from "@/i18n-js/instance";

import { SettingsSection } from "./SettingsSection";
import { SettingsSectionBody } from "./SettingsSectionBody";

interface PlanFeatureFlags {
  internal_custom_html_enabled: boolean;
}

const hasInternalCustomHtmlEnabled = (
  flags: unknown,
): flags is PlanFeatureFlags =>
  typeof (flags as PlanFeatureFlags | undefined)
    ?.internal_custom_html_enabled === "boolean";

export const AdvancedSection = () => {
  const { control } = useFormContext();
  const { currentCommunityMember } = usePunditUserContext();
  const isAdmin = isCommunityAdmin(currentCommunityMember);
  const {
    data: planFeatureFlags = {},
    isInitialLoading: isPlanFeatureFlagsLoading,
  } = usePlanFeatureFlags({
    enabled: isAdmin,
  });

  if (!isAdmin) {
    return null;
  }

  if (isPlanFeatureFlagsLoading) {
    return <Loader />;
  }

  const isCustomHtmlEnabled = hasInternalCustomHtmlEnabled(planFeatureFlags)
    ? planFeatureFlags.internal_custom_html_enabled
    : false;

  if (!isCustomHtmlEnabled) {
    return null;
  }

  return (
    <SettingsSection title={t("quick_post_v3.settings.sections.advanced")}>
      <SettingsSectionBody>
        <FormField
          layout="vertical"
          label={t("post.internal_custom_html")}
          description={t("post.internal_custom_html_description")}
        >
          <Controller
            control={control}
            name="internal_custom_html"
            render={({ field }) => {
              const value = typeof field.value === "string" ? field.value : "";

              return (
                <CodeInput
                  indentOnTab
                  language="html"
                  maxLines={8}
                  minLines={4}
                  onBlur={field.onBlur}
                  onValueChange={nextValue => field.onChange(nextValue)}
                  value={value}
                />
              );
            }}
          />
        </FormField>
      </SettingsSectionBody>
    </SettingsSection>
  );
};
